/**
 * useEventsInit — lightweight hook that populates the events store
 * (eventTypes, members, me) without the full EventsView orchestration.
 *
 * Used by AppLayout when actionType=fieldMapping, since EventsView never
 * renders in that mode but Settings still needs event data.
 *
 * Idempotent: only runs once (and only if the store isn't already
 * initialized by useEvents).
 *
 * @param enabled  Pass `false` to skip initialization (e.g. in normal app mode
 *                 where useEvents in EventsView handles it).
 */

import { useEffect, useRef } from 'react';
import { useModuleStore } from '@/stores/module-store';
import { useEventsStore } from '@/stores/events-store';
import type { IEventTPAModule } from '@/types/events';

function isEventModule(mod: unknown): mod is IEventTPAModule {
  return (
    mod != null &&
    typeof mod === 'object' &&
    typeof (mod as IEventTPAModule).getMe === 'function' &&
    typeof (mod as IEventTPAModule).getEventTypes === 'function' &&
    typeof (mod as IEventTPAModule).getScheduledEvents === 'function'
  );
}

export function useEventsInit(enabled = true) {
  const tpaModule = useModuleStore((s) => s.tpaModule);
  const tpaLoading = useModuleStore((s) => s.tpaLoading);
  const tpaAuthenticated = useModuleStore((s) => s.tpaAuthenticated);

  const initialized = useEventsStore((s) => s.initialized);
  const setMe = useEventsStore((s) => s.setMe);
  const setMembers = useEventsStore((s) => s.setMembers);
  const setEventTypes = useEventsStore((s) => s.setEventTypes);
  const setMembersLoading = useEventsStore((s) => s.setMembersLoading);
  const setEventTypesLoading = useEventsStore((s) => s.setEventTypesLoading);
  const setLoading = useEventsStore((s) => s.setLoading);
  const setError = useEventsStore((s) => s.setError);
  const setInitialized = useEventsStore((s) => s.setInitialized);

  const initRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    // Skip if already initialized (by useEvents in EventsView or a previous run)
    if (initialized || initRef.current) return;
    if (!tpaModule || tpaLoading || !tpaAuthenticated) return;

    const eventModule = isEventModule(tpaModule) ? tpaModule : null;
    if (!eventModule) return;

    initRef.current = true;

    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 2500;

    const doInit = async (attempt = 1) => {
      setLoading(true);
      setMembersLoading(true);
      setEventTypesLoading(true);
      setError(null);

      try {
        // 1. Get "me"
        const meData = await eventModule.getMe();
        setMe(meData);

        // 2. Fetch members + event types in parallel
        const [members, eventTypes] = await Promise.all([
          eventModule.getMembers(),
          eventModule.getEventTypes(meData.uri),
        ]);

        setMembers(members);
        setMembersLoading(false);
        setEventTypes(eventTypes);
        setEventTypesLoading(false);

        // 3. Mark as initialized
        setLoading(false);
        setInitialized(true);

        console.log('[useEventsInit] Events store initialized for settings (eventTypes:', eventTypes.length, ', members:', members.length, ')');
      } catch (err) {
        console.error(`[useEventsInit] Init failed (attempt ${attempt}/${MAX_RETRIES}):`, err);

        if (attempt < MAX_RETRIES) {
          console.log(`[useEventsInit] Retrying in ${RETRY_DELAY_MS}ms...`);
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
          return doInit(attempt + 1);
        }

        setError(err instanceof Error ? err.message : 'Failed to initialise events');
        setLoading(false);
        setMembersLoading(false);
        setEventTypesLoading(false);
      }
    };

    doInit();
  }, [
    enabled,
    tpaModule,
    tpaLoading,
    tpaAuthenticated,
    initialized,
    setMe,
    setMembers,
    setEventTypes,
    setMembersLoading,
    setEventTypesLoading,
    setLoading,
    setError,
    setInitialized,
  ]);
}
